import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
    Alert, Button, Card, Col, DatePicker, Descriptions, Input, InputNumber, Modal, Radio, Row, Segmented, Select, Space, Steps, Table, Tag, Typography, message
} from 'antd';
import { CalendarOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { api, errorText, hasRole, money, monthRangeOf, mulRound, round2, ruDate, showError, useSub } from './shared';
import WorksTable from './WorksTable';
import CoefficientEditor from './CoefficientEditor';

const { RangePicker } = DatePicker;
const { Text } = Typography;

/* Та же формула, что priceItem на сервере: сумма_работ × k с точным умножением */
const subAmount = (w, k) => mulRound(w.amount, k, 2);

const monthBounds = ym => {
    const d = dayjs(`${ym}-01`);
    return { from: d.startOf('month').format('YYYY-MM-DD'), to: d.endOf('month').format('YYYY-MM-DD') };
};

let draftSeq = 0;
const nextDraftId = () => `d${Date.now()}_${++draftSeq}`;

const FormTab = ({ preset, onFormed }) => {
    const { me, meta, subcontractors, settings } = useSub();
    const canForm = hasRole(me, 'sdo');
    const [range, setRange] = useState([dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')]);
    const [region, setRegion] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [subcontractorId, setSubcontractorId] = useState(null);
    const [vatMode, setVatMode] = useState('none');
    const [vatRate, setVatRate] = useState(null);
    const [vatModal, setVatModal] = useState(false);
    const [vatDraft, setVatDraft] = useState(20);
    const [actDate, setActDate] = useState(dayjs());
    const [state, setState] = useState('all');
    const [search, setSearch] = useState('');
    const [data, setData] = useState({ items: [], summary: {} });
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState([]);
    const [coef, setCoef] = useState(null);
    const [forming, setForming] = useState(false);
    const [wizard, setWizard] = useState(null);

    useEffect(() => {
        if (!preset) return;
        if (preset.period) setRange(monthRangeOf(preset.period));
        if (preset.state) setState(preset.state);
    }, [preset]);

    const period = range[0].format('YYYY-MM');

    const loadCoef = useCallback(async () => {
        try {
            const { data: d } = await api.get('/coefficients', { params: { period } });
            setCoef(d.current);
        } catch (error) {
            showError(error);
        }
    }, [period]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get('/works', {
                params: {
                    dateFrom: range[0].format('YYYY-MM-DD'),
                    dateTo: range[1].format('YYYY-MM-DD'),
                    region: region || undefined,
                    customer: customer || undefined,
                    subcontractorId: subcontractorId || undefined
                }
            });
            setData(d);
            setSelected([]);
        } catch (error) {
            showError(error);
        } finally {
            setLoading(false);
        }
    }, [range, region, customer, subcontractorId]);

    useEffect(() => {
        load();
        loadCoef();
    }, [load, loadCoef]);

    const items = useMemo(() => {
        let list = data.items || [];
        if (state !== 'all') list = list.filter(w => w.state === state);
        if (search) {
            const q = search.toLowerCase();
            list = list.filter(w => [w.request_number, w.object_number, w.executor, w.work_name, w.subcontractor_name, w.object_address]
                .some(v => String(v || '').toLowerCase().includes(q)));
        }
        return list;
    }, [data, state, search]);

    const s = data.summary || {};
    const readyItems = (data.items || []).filter(w => w.state === 'ready');
    const chosen = selected.length ? readyItems.filter(w => selected.includes(w.key)) : readyItems;
    const chosenAmount = chosen.reduce((sum, w) => sum + w.amount, 0);
    const coefMin = Number(settings?.coefficient_min ?? 0.8);
    const coefMax = Number(settings?.coefficient_max ?? 0.9);
    const workByKey = useMemo(() => new Map(chosen.map(w => [w.key, w])), [chosen]);
    const groups = useMemo(() => {
        const map = new Map();
        chosen.forEach(w => {
            const gk = `${w.subcontractor_id}|${w.region}`;
            if (!map.has(gk)) {
                const sub = subcontractors.find(x => x.id === w.subcontractor_id);
                const own = sub?.coefficient != null ? Number(sub.coefficient) : null;
                map.set(gk, { key: gk, name: w.subcontractor_name || sub?.short_name || '—', region: w.region, own, works: [] });
            }
            map.get(gk).works.push(w);
        });
        return [...map.values()];
    }, [chosen, subcontractors]);
    const effectiveCoefs = [...new Set(groups.map(g => g.own ?? coef?.value).filter(v => v != null))];
    const defaultCoef = effectiveCoefs.length === 1 ? effectiveCoefs[0] : coef?.value ?? null;

    const buildDrafts = useCallback((mode = 'one') => {
        const from = range[0].format('YYYY-MM-DD');
        const to = range[1].format('YYYY-MM-DD');
        const day = actDate.format('YYYY-MM-DD');
        if (mode === 'months') {
            const out = [];
            groups.forEach(g => {
                const byMonth = new Map();
                g.works.forEach(w => {
                    const ym = String(w.done_date || from).slice(0, 7);
                    if (!byMonth.has(ym)) byMonth.set(ym, []);
                    byMonth.get(ym).push(w.key);
                });
                [...byMonth.keys()].sort().forEach(ym => {
                    const b = monthBounds(ym);
                    out.push({
                        id: nextDraftId(),
                        groupKey: g.key,
                        name: g.name,
                        region: g.region,
                        dateFrom: b.from,
                        dateTo: b.to,
                        actDate: day,
                        keys: byMonth.get(ym)
                    });
                });
            });
            return out;
        }
        return groups.map(g => ({
            id: nextDraftId(),
            groupKey: g.key,
            name: g.name,
            region: g.region,
            dateFrom: from,
            dateTo: to,
            actDate: day,
            keys: g.works.map(w => w.key)
        }));
    }, [groups, range, actDate]);

    const wizardRows = useMemo(() => {
        if (!wizard) return [];
        const k = wizard.coef;
        const rate = vatMode === 'with' ? Number(vatRate) || 0 : 0;
        return groups.map(g => {
            let base = 0;
            let noVat = 0;
            let vat = 0;
            g.works.forEach(w => {
                const a = subAmount(w, k);
                base += Number(w.amount) || 0;
                noVat += a;
                vat += rate > 0 ? round2(a * rate / 100) : 0;
            });
            return { ...g, count: g.works.length, base: round2(base), noVat: round2(noVat), vat: round2(vat), total: round2(noVat + vat) };
        });
    }, [wizard, groups, vatMode, vatRate]);
    const wizardSum = f => round2(wizardRows.reduce((sum, r) => sum + r[f], 0));
    const coefValid = !!wizard && /^\d+(\.\d{1,20})?$/.test(String(wizard.coef ?? ''))
        && Number(wizard.coef) >= coefMin && Number(wizard.coef) <= coefMax;

    const draftStats = useMemo(() => {
        if (!wizard?.drafts) return { rows: [], unassigned: [], duplicates: [], empty: [] };
        const k = wizard.coef;
        const rate = vatMode === 'with' ? Number(vatRate) || 0 : 0;
        const seen = new Map();
        const duplicates = [];
        const rows = wizard.drafts.map((d, idx) => {
            let base = 0;
            let noVat = 0;
            let vat = 0;
            const works = [];
            d.keys.forEach(key => {
                if (seen.has(key)) duplicates.push(key);
                else seen.set(key, d.id);
                const w = workByKey.get(key);
                if (!w) return;
                works.push(w);
                const a = subAmount(w, k);
                base += Number(w.amount) || 0;
                noVat += a;
                vat += rate > 0 ? round2(a * rate / 100) : 0;
            });
            return {
                ...d,
                index: idx + 1,
                works,
                count: works.length,
                base: round2(base),
                noVat: round2(noVat),
                vat: round2(vat),
                total: round2(noVat + vat)
            };
        });
        const assigned = new Set(wizard.drafts.flatMap(d => d.keys));
        const unassigned = chosen.filter(w => !assigned.has(w.key));
        const empty = rows.filter(r => !r.count);
        return { rows, unassigned, duplicates: [...new Set(duplicates)], empty };
    }, [wizard, workByKey, chosen, vatMode, vatRate]);

    const splitValid = !!wizard && coefValid
        && draftStats.rows.length > 0
        && !draftStats.unassigned.length
        && !draftStats.duplicates.length
        && !draftStats.empty.length
        && draftStats.rows.every(d => d.dateFrom && d.dateTo && d.dateFrom <= d.dateTo && d.actDate);

    const patchDraft = (id, patch) => setWizard(w => ({
        ...w,
        drafts: w.drafts.map(d => (d.id === id ? { ...d, ...patch } : d))
    }));

    const moveWork = (key, toId) => setWizard(w => ({
        ...w,
        drafts: w.drafts.map(d => {
            const keys = d.keys.filter(k => k !== key);
            if (d.id === toId && !keys.includes(key)) keys.push(key);
            return { ...d, keys };
        })
    }));

    const addDraft = groupKey => {
        const g = groups.find(x => x.key === groupKey);
        if (!g) return;
        setWizard(w => ({
            ...w,
            drafts: [
                ...w.drafts,
                {
                    id: nextDraftId(),
                    groupKey,
                    name: g.name,
                    region: g.region,
                    dateFrom: range[0].format('YYYY-MM-DD'),
                    dateTo: range[1].format('YYYY-MM-DD'),
                    actDate: actDate.format('YYYY-MM-DD'),
                    keys: []
                }
            ]
        }));
    };

    const removeDraft = id => setWizard(w => {
        const target = w.drafts.find(d => d.id === id);
        if (!target) return w;
        const same = w.drafts.filter(d => d.groupKey === target.groupKey);
        if (same.length <= 1) {
            message.warning('У субподрядчика должен остаться хотя бы один акт');
            return w;
        }
        const fallback = same.find(d => d.id !== id);
        return {
            ...w,
            drafts: w.drafts
                .filter(d => d.id !== id)
                .map(d => (d.id === fallback.id ? { ...d, keys: [...d.keys, ...target.keys] } : d))
        };
    });

    const goStep = step => setWizard(w => {
        if (step === 2 && (!w.drafts || !w.drafts.length)) {
            return { ...w, step, drafts: buildDrafts('one') };
        }
        return { ...w, step };
    });

    const doForm = () => {
        if (!chosen.length) {
            message.warning('Нет работ, готовых к формированию');
            return;
        }
        if (vatMode === 'with' && !(vatRate > 0)) {
            setVatModal(true);
            return;
        }
        setWizard({
            step: 0,
            coef: defaultCoef == null ? null : String(defaultCoef),
            drafts: buildDrafts('one')
        });
    };

    const submitForm = async () => {
        if (!splitValid) {
            message.warning('Проверьте разбивку: все работы должны быть в актах, периоды и даты заполнены');
            return;
        }
        setForming(true);
        try {
            const { data: d } = await api.post('/acts/form', {
                dateFrom: range[0].format('YYYY-MM-DD'),
                dateTo: range[1].format('YYYY-MM-DD'),
                region,
                customer,
                subcontractorId,
                vatMode,
                vatRate,
                actDate: actDate.format('YYYY-MM-DD'),
                keys: chosen.map(w => w.key),
                coefficient: wizard.coef,
                batches: wizard.drafts.map(x => ({
                    keys: x.keys,
                    dateFrom: x.dateFrom,
                    dateTo: x.dateTo,
                    actDate: x.actDate
                }))
            });
            setWizard(null);
            message.success(`Сформировано актов: ${d.created.length} — ${d.created.map(a => a.number).join(', ')}`);
            await load();
            onFormed?.(d.created);
        } catch (error) {
            Modal.error({ title: 'Акт не сформирован', content: errorText(error) });
            load();
        } finally {
            setForming(false);
        }
    };

    const footer = (() => {
        if (!wizard) return null;
        if (wizard.step === 0) {
            return [
                <Button key="cancel" onClick={() => setWizard(null)}>Отмена</Button>,
                <Button key="next" type="primary" onClick={() => goStep(1)}>Далее</Button>
            ];
        }
        if (wizard.step === 1) {
            return [
                <Button key="back" onClick={() => goStep(0)}>Назад</Button>,
                <Button key="next" type="primary" disabled={!coefValid} onClick={() => goStep(2)}>Далее</Button>
            ];
        }
        return [
            <Button key="back" disabled={forming} onClick={() => goStep(1)}>Назад</Button>,
            <Button key="form" type="primary" icon={<ThunderboltOutlined />} loading={forming}
                disabled={!splitValid} onClick={submitForm}>
                Сформировать акты
            </Button>
        ];
    })();

    const groupDrafts = groupKey => (draftStats.rows || []).filter(d => d.groupKey === groupKey);

    return (
        <>
            <Card size="small" style={{ marginBottom: 12 }}>
                <Row gutter={[12, 12]} align="middle">
                    <Col>
                        <Text type="secondary">Период с / по</Text>
                        <div>
                            <RangePicker value={range} onChange={v => v && setRange(v)} format="DD.MM.YYYY" allowClear={false} />
                        </div>
                    </Col>
                    <Col>
                        <Text type="secondary">Регион</Text>
                        <div>
                            <Select allowClear showSearch placeholder="Все" style={{ width: 200 }} value={region} onChange={setRegion}
                                options={(meta.regions || []).map(r => ({ value: r, label: r }))} />
                        </div>
                    </Col>
                    <Col>
                        <Text type="secondary">Заказчик</Text>
                        <div>
                            <Select allowClear showSearch placeholder="Все" style={{ width: 240 }} value={customer} onChange={setCustomer}
                                options={(meta.customers || []).map(c => ({ value: c, label: c }))} />
                        </div>
                    </Col>
                    <Col>
                        <Text type="secondary">Субподрядчик</Text>
                        <div>
                            <Select allowClear showSearch optionFilterProp="label" placeholder="Все" style={{ width: 220 }}
                                value={subcontractorId} onChange={setSubcontractorId}
                                options={subcontractors.map(x => ({ value: x.id, label: x.short_name || x.full_name }))} />
                        </div>
                    </Col>
                    <Col>
                        <Text type="secondary">НДС</Text>
                        <div>
                            <Radio.Group
                                value={vatMode}
                                onChange={e => {
                                    setVatMode(e.target.value);
                                    if (e.target.value === 'with') {
                                        setVatDraft(vatRate || 20);
                                        setVatModal(true);
                                    }
                                }}
                            >
                                <Radio.Button value="none">Без НДС</Radio.Button>
                                <Radio.Button value="with">С НДС{vatMode === 'with' && vatRate ? ` ${vatRate}%` : ''}</Radio.Button>
                            </Radio.Group>
                        </div>
                    </Col>
                    <Col>
                        <Text type="secondary">Дата акта</Text>
                        <div>
                            <DatePicker value={actDate} onChange={v => v && setActDate(v)} format="DD.MM.YYYY" allowClear={false} />
                        </div>
                    </Col>
                    <Col>
                        <Text type="secondary">Коэффициент {period}</Text>
                        <div>
                            <Space>
                                <Tag color="blue" style={{ fontSize: 14, padding: '2px 8px' }}>{coef?.value ?? '—'}</Tag>
                                {coef?.source === 'inherited' && <Text type="secondary">с {coef.period}</Text>}
                                {coef?.source === 'default' && <Text type="secondary">по умолч.</Text>}
                                {canForm && <CoefficientEditor period={period} current={coef?.value} settings={settings} onChanged={loadCoef} />}
                            </Space>
                        </div>
                    </Col>
                    <Col flex="auto" style={{ textAlign: 'right' }}>
                        <Space>
                            <Button icon={<ReloadOutlined />} onClick={load}>Обновить</Button>
                            {canForm && (
                                <Button type="primary" size="large" icon={<ThunderboltOutlined />} loading={forming} onClick={doForm}>
                                    СФОРМИРОВАТЬ
                                </Button>
                            )}
                        </Space>
                    </Col>
                </Row>
            </Card>

            {!canForm && <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Формировать акты может только СДО. Вам доступен просмотр." />}

            <Space style={{ marginBottom: 8 }} wrap>
                <Segmented
                    value={state}
                    onChange={setState}
                    options={[
                        { value: 'all', label: `Все (${s.total || 0})` },
                        { value: 'ready', label: `Готово (${s.ready || 0})` },
                        { value: 'error', label: `Требует уточнения (${s.errors || 0})` },
                        { value: 'closed', label: `Закрыто (${s.closed || 0})` }
                    ]}
                />
                <Input.Search allowClear placeholder="Заявка, объект, исполнитель, работа" style={{ width: 320 }} onSearch={setSearch} onChange={e => !e.target.value && setSearch('')} />
                <Text>
                    К формированию: <b>{chosen.length}</b> работ на <b>{money(chosenAmount)} ₽</b>
                    {coef?.value ? <> → субподряд ≈ <b>{money(chosenAmount * coef.value)} ₽</b></> : null}
                    {selected.length ? <Button type="link" size="small" onClick={() => setSelected([])}>сбросить выбор</Button> : null}
                </Text>
            </Space>

            <WorksTable items={items} loading={loading} selectable={canForm} selectedKeys={selected} onSelect={setSelected} />

            <Modal
                open={!!wizard}
                width={wizard?.step === 2 ? 1080 : 820}
                title="Формирование актов"
                maskClosable={false}
                onCancel={() => !forming && setWizard(null)}
                footer={footer}
            >
                {wizard && (
                    <>
                        <Steps size="small" current={wizard.step} style={{ marginBottom: 16 }}
                            items={[{ title: 'Сумма работ' }, { title: 'Коэффициент' }, { title: 'Разбивка на акты' }]} />
                        {wizard.step === 0 && (
                            <>
                                <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
                                    <Descriptions.Item label="Период фильтра">{range[0].format('DD.MM.YYYY')} – {range[1].format('DD.MM.YYYY')}</Descriptions.Item>
                                    <Descriptions.Item label="Дата акта (по умолч.)">{actDate.format('DD.MM.YYYY')}</Descriptions.Item>
                                    <Descriptions.Item label="Работ">{chosen.length}{selected.length ? ' (выбранные)' : ' (все готовые)'}</Descriptions.Item>
                                    <Descriptions.Item label="НДС">{vatMode === 'with' ? `${vatRate}%` : 'без НДС'}</Descriptions.Item>
                                    <Descriptions.Item label="Сумма работ (заказчик)" span={2}><b style={{ fontSize: 16 }}>{money(chosenAmount)} ₽</b></Descriptions.Item>
                                </Descriptions>
                                <Table size="small" pagination={false} rowKey="key" dataSource={wizardRows}
                                    columns={[
                                        { title: 'Субподрядчик', dataIndex: 'name' },
                                        { title: 'Регион', dataIndex: 'region' },
                                        { title: 'Работ', dataIndex: 'count', align: 'right', width: 80 },
                                        { title: 'Сумма работ, ₽', dataIndex: 'base', align: 'right', render: v => money(v) }
                                    ]}
                                    summary={() => (
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={2}><b>Групп: {wizardRows.length}</b></Table.Summary.Cell>
                                            <Table.Summary.Cell index={2} align="right"><b>{chosen.length}</b></Table.Summary.Cell>
                                            <Table.Summary.Cell index={3} align="right"><b>{money(wizardSum('base'))}</b></Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    )}
                                />
                            </>
                        )}
                        {wizard.step === 1 && (
                            <>
                                <Space align="center" style={{ marginBottom: 12 }} wrap>
                                    <Text strong>Коэффициент для всех работ:</Text>
                                    <InputNumber stringMode min={String(coefMin)} max={String(coefMax)} step="0.001" style={{ width: 280 }}
                                        status={wizard.coef && !coefValid ? 'error' : undefined}
                                        value={wizard.coef} onChange={v => setWizard(w => ({ ...w, coef: v }))} autoFocus />
                                    <Text type="secondary">до 20 знаков после запятой; допустимо {coefMin}–{coefMax}; коэффициент периода {coef?.value ?? '—'}</Text>
                                </Space>
                                {wizard.coef && !coefValid && (
                                    <Alert type="error" showIcon style={{ marginBottom: 12 }}
                                        message={`Коэффициент должен быть в диапазоне ${coefMin}–${coefMax}, не более 20 знаков после запятой`} />
                                )}
                                {groups.some(g => g.own != null && g.own !== Number(wizard.coef)) && (
                                    <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                                        message={`Свой коэффициент в справочнике: ${groups.filter(g => g.own != null && g.own !== Number(wizard.coef)).map(g => `${g.name} — ${g.own}`).join('; ')}. В этих актах будет применён ${wizard.coef ?? '—'}.`} />
                                )}
                                <Table size="small" pagination={false} rowKey="key" dataSource={wizardRows}
                                    columns={[
                                        { title: 'Субподрядчик / регион', key: 'name', render: (_, r) => <div>{r.name}<div><Text type="secondary">{r.region}</Text></div></div> },
                                        { title: 'Работ', dataIndex: 'count', align: 'right', width: 70 },
                                        { title: 'Сумма работ, ₽', dataIndex: 'base', align: 'right', render: v => money(v) },
                                        { title: 'Субподряд без НДС, ₽', dataIndex: 'noVat', align: 'right', render: v => money(v) },
                                        ...(vatMode === 'with' ? [{ title: `НДС ${vatRate}%, ₽`, dataIndex: 'vat', align: 'right', render: v => money(v) }] : []),
                                        { title: 'Итого, ₽', dataIndex: 'total', align: 'right', render: v => <b>{money(v)}</b> }
                                    ]}
                                    summary={() => (
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0}><b>Итого</b></Table.Summary.Cell>
                                            <Table.Summary.Cell index={1} align="right"><b>{chosen.length}</b></Table.Summary.Cell>
                                            <Table.Summary.Cell index={2} align="right"><b>{money(wizardSum('base'))}</b></Table.Summary.Cell>
                                            <Table.Summary.Cell index={3} align="right"><b>{money(wizardSum('noVat'))}</b></Table.Summary.Cell>
                                            {vatMode === 'with' && <Table.Summary.Cell index={4} align="right"><b>{money(wizardSum('vat'))}</b></Table.Summary.Cell>}
                                            <Table.Summary.Cell index={5} align="right"><b>{money(wizardSum('total'))}</b></Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    )}
                                />
                                <div style={{ marginTop: 8 }}>
                                    <Text type="secondary">Разница с суммой работ: {money(round2(wizardSum('base') - wizardSum('noVat')))} ₽</Text>
                                </div>
                            </>
                        )}
                        {wizard.step === 2 && (
                            <>
                                <Space wrap style={{ marginBottom: 12 }}>
                                    <Button icon={<CalendarOutlined />} onClick={() => setWizard(w => ({ ...w, drafts: buildDrafts('months') }))}>
                                        Разбить по месяцам
                                    </Button>
                                    <Button onClick={() => setWizard(w => ({ ...w, drafts: buildDrafts('one') }))}>
                                        Один акт на субподрядчика
                                    </Button>
                                    <Text type="secondary">
                                        Период акта задаётся свободно (как в печатной форме). У каждого акта своя дата. Номер — автоматически.
                                    </Text>
                                </Space>
                                {draftStats.unassigned.length ? (
                                    <Alert type="error" showIcon style={{ marginBottom: 12 }}
                                        message={`Не распределено работ: ${draftStats.unassigned.length}. Перенесите их в один из актов.`} />
                                ) : null}
                                {draftStats.duplicates.length ? (
                                    <Alert type="error" showIcon style={{ marginBottom: 12 }}
                                        message="Одна и та же работа попала в несколько актов — исправьте разбивку" />
                                ) : null}
                                {draftStats.empty.length ? (
                                    <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                                        message={`Пустых актов: ${draftStats.empty.length}. Удалите их или перенесите туда работы.`} />
                                ) : null}

                                {groups.map(g => {
                                    const drafts = groupDrafts(g.key);
                                    return (
                                        <Card key={g.key} size="small" style={{ marginBottom: 12 }}
                                            title={<span>{g.name} · {g.region} · работ {g.works.length}</span>}
                                            extra={
                                                <Button size="small" icon={<PlusOutlined />} onClick={() => addDraft(g.key)}>
                                                    Добавить акт
                                                </Button>
                                            }
                                        >
                                            {drafts.map(d => (
                                                <Card key={d.id} size="small" type="inner" style={{ marginBottom: 8 }}
                                                    title={
                                                        <Space wrap>
                                                            <Text strong>Акт {d.index}</Text>
                                                            <Text type="secondary">{d.count} раб. · {money(d.base)} ₽ → {money(d.total)} ₽</Text>
                                                        </Space>
                                                    }
                                                    extra={
                                                        drafts.length > 1 ? (
                                                            <Button size="small" danger type="text" icon={<DeleteOutlined />}
                                                                onClick={() => removeDraft(d.id)} />
                                                        ) : null
                                                    }
                                                >
                                                    <Space wrap style={{ marginBottom: 8 }}>
                                                        <span>
                                                            <Text type="secondary">Период акта </Text>
                                                            <RangePicker
                                                                value={[dayjs(d.dateFrom), dayjs(d.dateTo)]}
                                                                format="DD.MM.YYYY"
                                                                allowClear={false}
                                                                onChange={v => v && patchDraft(d.id, {
                                                                    dateFrom: v[0].format('YYYY-MM-DD'),
                                                                    dateTo: v[1].format('YYYY-MM-DD')
                                                                })}
                                                            />
                                                        </span>
                                                        <span>
                                                            <Text type="secondary">Дата акта </Text>
                                                            <DatePicker
                                                                value={dayjs(d.actDate)}
                                                                format="DD.MM.YYYY"
                                                                allowClear={false}
                                                                onChange={v => v && patchDraft(d.id, { actDate: v.format('YYYY-MM-DD') })}
                                                            />
                                                        </span>
                                                    </Space>
                                                    <Table
                                                        size="small"
                                                        pagination={d.works.length > 8 ? { pageSize: 8 } : false}
                                                        rowKey="key"
                                                        dataSource={d.works}
                                                        columns={[
                                                            { title: 'Дата', dataIndex: 'done_date', width: 95, render: ruDate },
                                                            { title: 'Заявка', dataIndex: 'request_number', width: 110 },
                                                            { title: 'Объект', dataIndex: 'object_number', width: 90 },
                                                            { title: 'Работа', dataIndex: 'work_name', ellipsis: true },
                                                            { title: 'Сумма', dataIndex: 'amount', width: 100, align: 'right', render: money },
                                                            {
                                                                title: 'В акт',
                                                                key: 'move',
                                                                width: 150,
                                                                render: (_, w) => (
                                                                    <Select
                                                                        size="small"
                                                                        style={{ width: 130 }}
                                                                        value={d.id}
                                                                        options={drafts.map(x => ({
                                                                            value: x.id,
                                                                            label: `Акт ${x.index}`
                                                                        }))}
                                                                        onChange={toId => moveWork(w.key, toId)}
                                                                    />
                                                                )
                                                            }
                                                        ]}
                                                    />
                                                </Card>
                                            ))}
                                        </Card>
                                    );
                                })}

                                <Descriptions size="small" bordered column={3}>
                                    <Descriptions.Item label="Актов">{draftStats.rows.length}</Descriptions.Item>
                                    <Descriptions.Item label="Работ">{chosen.length - draftStats.unassigned.length} / {chosen.length}</Descriptions.Item>
                                    <Descriptions.Item label="Субподряд">{money(round2(draftStats.rows.reduce((sum, r) => sum + r.total, 0)))} ₽</Descriptions.Item>
                                </Descriptions>
                            </>
                        )}
                    </>
                )}
            </Modal>

            <Modal
                open={vatModal}
                title="Ставка НДС"
                okText="Применить"
                cancelText="Без НДС"
                onOk={() => {
                    if (!(vatDraft > 0)) return message.warning('Укажите ставку');
                    setVatRate(vatDraft);
                    setVatModal(false);
                    return undefined;
                }}
                onCancel={() => {
                    setVatModal(false);
                    if (!vatRate) setVatMode('none');
                }}
            >
                <Space>
                    <InputNumber min={0.01} max={30} step={1} value={vatDraft} onChange={setVatDraft} addonAfter="%" />
                    {[5, 7, 10, 20, 22].map(r => <Button key={r} size="small" onClick={() => setVatDraft(r)}>{r}%</Button>)}
                </Space>
            </Modal>
        </>
    );
};

export default FormTab;
